import React from 'react';
import { cva } from 'class-variance-authority';
import clsx from 'clsx';

import { Icon } from './Icon';
import { useControllable } from '../../hooks/useControllable';
import styles from './Pagination.module.css';

// ── CVA ──────────────────────────────────────────────────────────────────────

const pageBtn = cva(styles.btn, {
  variants: {
    active: { true: styles.active },
  },
});

// ── Types ─────────────────────────────────────────────────────────────────────

interface PaginationProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  /** Controlled current page */
  current?: number;
  /** Default page */
  defaultCurrent?: number;
  /** Total items */
  total: number;
  /** Items per page */
  pageSize?: number;
  /** Page change handler */
  onChange?: (page: number) => void;
  /** Disabled */
  disabled?: boolean;
  /** Simple mode (just prev/next + page indicator) */
  simple?: boolean;
}

/**
 * Pagination — page navigation for large datasets.
 *
 * | Prop | Type | Default | Description |
 * |------|------|---------|-------------|
 * | current | number | — | Controlled page |
 * | defaultCurrent | number | 1 | Initial page |
 * | total | number | — | Total items |
 * | pageSize | number | 10 | Items per page |
 * | onChange | (page) => void | — | Page change handler |
 * | disabled | boolean | false | Disable |
 * | simple | boolean | false | Minimal mode |
 *
 * @example
 * <Pagination total={100} />
 * <Pagination total={500} pageSize={20} />
 */
export function Pagination({
  current: controlledCurrent,
  defaultCurrent = 1,
  total,
  pageSize = 10,
  onChange,
  disabled = false,
  simple = false,
  style,
  className,
  ...props
}: PaginationProps) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const [current, setCurrent] = useControllable(controlledCurrent, defaultCurrent, onChange);

  const goTo = (page: number) => {
    if (disabled) return;
    const clamped = Math.min(totalPages, Math.max(1, page));
    setCurrent(clamped);
  };

  if (simple) {
    return (
      <div className={clsx(styles.rootSimple, className)} style={style} {...props}>
        <button
          className={pageBtn({ active: false })}
          onClick={() => goTo(current - 1)}
          disabled={disabled || current <= 1}
        >
          <Icon name="chevron_left" size={16} />
        </button>
        <span className={styles.pageIndicator}>
          {current} / {totalPages}
        </span>
        <button
          className={pageBtn({ active: false })}
          onClick={() => goTo(current + 1)}
          disabled={disabled || current >= totalPages}
        >
          <Icon name="chevron_right" size={16} />
        </button>
      </div>
    );
  }

  // Build page numbers with ellipsis
  const pages: (number | '...')[] = [];
  const delta = 2;
  for (let i = 1; i <= totalPages; i++) {
    if (i === 1 || i === totalPages || (i >= current - delta && i <= current + delta)) {
      pages.push(i);
    } else if (pages[pages.length - 1] !== '...') {
      pages.push('...');
    }
  }

  return (
    <div className={clsx(styles.root, className)} style={style} {...props}>
      <button
        className={pageBtn({ active: false })}
        onClick={() => goTo(current - 1)}
        disabled={disabled || current <= 1}
      >
        <Icon name="chevron_left" size={16} />
      </button>
      {pages.map((p, i) =>
        p === '...' ? (
          <span key={`e${i}`} className={styles.ellipsis}>
            ...
          </span>
        ) : (
          <button
            key={p}
            className={pageBtn({ active: p === current })}
            onClick={() => goTo(p)}
            disabled={disabled}
          >
            {p}
          </button>
        ),
      )}
      <button
        className={pageBtn({ active: false })}
        onClick={() => goTo(current + 1)}
        disabled={disabled || current >= totalPages}
      >
        <Icon name="chevron_right" size={16} />
      </button>
    </div>
  );
}
